import { useEffect, useMemo, useState } from 'react';
import { Plus, FileCheck, Upload, ChevronDown, ChevronUp } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import SearchInput from '../components/SearchInput';
import { termClaimsAPI, termCoveragesAPI } from '../services/api';
import { formatCurrency } from '../utils/formatters';
import { filterBySearch } from '../utils/search';

const STATUS_COLORS = {
  submitted: 'bg-blue-50 text-blue-700',
  under_review: 'bg-amber-50 text-amber-700',
  query_raised: 'bg-orange-50 text-orange-700',
  approved: 'bg-green-50 text-green-700',
  partially_approved: 'bg-yellow-50 text-yellow-700',
  rejected: 'bg-red-50 text-red-700',
  settled: 'bg-emerald-50 text-emerald-700',
  withdrawn: 'bg-slate-50 text-slate-600',
};

const STATUSES = ['submitted', 'under_review', 'query_raised', 'approved', 'partially_approved', 'rejected', 'settled', 'withdrawn'];
const DOC_TYPES = ['death_certificate', 'claim_form', 'nominee_kyc', 'bank_details', 'employer_letter', 'other'];

function TermClaimCard({ claim, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState(null);
  const [docType, setDocType] = useState('death_certificate');
  const [file, setFile] = useState(null);
  const [queryNotes, setQueryNotes] = useState('');
  const [queryResponse, setQueryResponse] = useState('');
  const [uploading, setUploading] = useState(false);

  const loadDetail = async () => {
    const { data } = await termClaimsAPI.get(claim.id);
    setDetail(data);
    setQueryNotes(data.query_notes || '');
    setQueryResponse(data.query_response || '');
  };

  const toggle = async () => {
    if (!expanded && !detail) await loadDetail();
    setExpanded(!expanded);
  };

  const updateStatus = async (status) => {
    await termClaimsAPI.updateStatus(claim.id, {
      status,
      query_notes: queryNotes || undefined,
      query_response: queryResponse || undefined,
    });
    onRefresh();
    if (expanded) loadDetail();
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('document', file);
    formData.append('document_type', docType);
    try {
      await termClaimsAPI.uploadDocument(claim.id, formData);
      setFile(null);
      await loadDetail();
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="card">
      <div className="flex items-start gap-3">
        <div className="bg-rose-50 p-2 rounded-lg"><FileCheck className="w-5 h-5 text-rose-600" /></div>
        <div className="flex-1">
          <div className="flex justify-between gap-2">
            <h3 className="font-semibold">{claim.claim_number}</h3>
            <span className={`px-2 py-0.5 text-xs rounded-full capitalize ${STATUS_COLORS[claim.status]}`}>{claim.status?.replace(/_/g, ' ')}</span>
          </div>
          <p className="text-sm text-slate-500">{claim.employee_name} · {claim.policy_number}</p>
          <p className="text-sm mt-1">Claim: {formatCurrency(claim.claim_amount)} · Sum Assured: {formatCurrency(claim.sum_assured)}</p>
          <p className="text-xs text-slate-400 mt-1">Nominee: {claim.nominee_name || '—'}</p>
          <button type="button" onClick={toggle} className="text-sm text-primary-600 mt-2 flex items-center gap-1">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {expanded ? 'Hide details' : 'Documents & status'}
          </button>

          {expanded && (
            <div className="mt-4 space-y-4 border-t pt-4">
              <div>
                <label className="label">Update Status</label>
                <select className="input-field text-xs" value={claim.status} onChange={(e) => updateStatus(e.target.value)}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                </select>
              </div>

              <div className="space-y-2">
                <div>
                  <label className="label">Query Notes</label>
                  <textarea className="input-field text-sm" rows={2} value={queryNotes} onChange={(e) => setQueryNotes(e.target.value)} />
                </div>
                <div>
                  <label className="label">Query Response</label>
                  <textarea className="input-field text-sm" rows={2} value={queryResponse} onChange={(e) => setQueryResponse(e.target.value)} />
                </div>
                <button type="button" onClick={() => updateStatus(claim.status)} className="btn-secondary text-sm">Save Notes</button>
              </div>

              {detail?.documents?.length > 0 && (
                <ul className="text-sm space-y-1">
                  {detail.documents.map((d) => (
                    <li key={d.id} className="text-slate-600">{d.document_type}: {d.file_name}</li>
                  ))}
                </ul>
              )}

              <form onSubmit={handleUpload} className="flex flex-wrap gap-2 items-end">
                <div>
                  <label className="label">Document Type</label>
                  <select className="input-field text-xs" value={docType} onChange={(e) => setDocType(e.target.value)}>
                    {DOC_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">File</label>
                  <input type="file" className="input-field text-xs" onChange={(e) => setFile(e.target.files?.[0])} />
                </div>
                <button type="submit" disabled={uploading || !file} className="btn-secondary text-sm flex items-center gap-1">
                  <Upload className="w-4 h-4" />{uploading ? 'Uploading…' : 'Upload'}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function TermClaims() {
  const [claims, setClaims] = useState([]);
  const [coverages, setCoverages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ coverage_id: '', claim_amount: '', claim_notes: '' });
  const [search, setSearch] = useState('');

  const filteredClaims = useMemo(
    () => filterBySearch(claims, search, [
      'claim_number',
      'employee_name',
      'policy_number',
      'status',
      'claim_notes',
    ]),
    [claims, search]
  );

  const load = () => {
    termClaimsAPI.list()
      .then(({ data }) => setClaims(data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    termCoveragesAPI.list().then(({ data }) => setCoverages(data));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await termClaimsAPI.create({
        coverage_id: parseInt(form.coverage_id, 10),
        claim_amount: parseFloat(form.claim_amount),
        claim_notes: form.claim_notes || undefined,
      });
      setShowForm(false);
      setForm({ coverage_id: '', claim_amount: '', claim_notes: '' });
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to submit term claim');
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Term Insurance Claims</h1>
          <p className="text-slate-500 mt-1">Death claim workflow with document upload and settlement tracking</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary flex items-center gap-2 self-start">
          <Plus className="w-4 h-4" />
          Submit Death Claim
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="card space-y-4">
          <h3 className="font-semibold">New Term Death Claim</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="label">Term Coverage *</label>
              <select className="input-field" required value={form.coverage_id} onChange={(e) => {
                const cov = coverages.find((c) => String(c.id) === e.target.value);
                setForm({
                  ...form,
                  coverage_id: e.target.value,
                  claim_amount: cov ? String(cov.sum_assured) : form.claim_amount,
                });
              }}>
                <option value="">Select</option>
                {coverages.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.employee_name} — {c.policy_number} ({formatCurrency(c.sum_assured)})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Claim Amount (INR) *</label>
              <input type="number" className="input-field" required value={form.claim_amount} onChange={(e) => setForm({ ...form, claim_amount: e.target.value })} />
            </div>
            <div className="md:col-span-3">
              <label className="label">Claim Notes</label>
              <textarea className="input-field" rows={2} value={form.claim_notes} onChange={(e) => setForm({ ...form, claim_notes: e.target.value })} />
            </div>
          </div>
          <div className="flex gap-3">
            <button type="submit" className="btn-primary">Submit Claim</button>
            <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </form>
      )}

      <SearchInput
        className="max-w-md"
        placeholder="Search by claim number, employee, policy, status…"
        value={search}
        onChange={setSearch}
      />

      <div className="space-y-4">
        {filteredClaims.map((c) => (
          <TermClaimCard key={c.id} claim={c} onRefresh={load} />
        ))}
        {claims.length === 0 && (
          <div className="card text-center py-12 text-slate-500">No term claims submitted yet.</div>
        )}
        {claims.length > 0 && filteredClaims.length === 0 && (
          <p className="text-center py-8 text-slate-500">No term claims match your search.</p>
        )}
      </div>
    </div>
  );
}
